import React, { useEffect, useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { LeftArrowWhite } from "../../assets/images/svg/LeftArrow";
import LcStartImg from "../../assets/images/LcStartImg.png";
import Webcam from "react-webcam";
import { observer } from "mobx-react";
import { applicationStore, applicationStore as store } from "../../store/ApplicationStore";
import * as cam from "@mediapipe/camera_utils";
import { faceActionMeta } from "../../utils/faceActionMeta";
import { getAction, resetStabilization, verifyActionOnImage, disposeImageLandmarker, warmUpImageLandmarker } from "../../helpers/mediaPipeHelper";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import { toJS } from "mobx";
import { toast } from "react-toastify";
import logger from "src/utils/logger";
import InconsistencyError from "src/components/ui/InconsistencyError";
import { workflowStepIterator } from "src/services/workflowStepIterator";
import { NodeTypes } from "src/enums/NodeTypes";
import { LivenessImageMovementPayloadValues } from "src/enums/LivenessImageMovementPayloadValues";
import { CameraPermissionStatuses } from "src/enums/CameraPermissionStatuses";
import { parseServiceErrorMessage } from "src/helpers/parseServiceError";
import { getOrientationType } from "src/helpers/getOrientationType";
import { useTranslation } from "react-i18next";
import { FLOW_HEX, FLOW_SHADOW, FLOW_VAR } from "src/theme/flowColorTokens";

const portraitConstraints = {
  width: { min: 480, ideal: 720, max: 1080 },
  height: { min: 640, ideal: 960, max: 1440 },
  facingMode: "user",
};

const landscapeConstraints = {
  width: { min: 640, ideal: 960, max: 1440 },
  height: { min: 480, ideal: 720, max: 1080 },
  facingMode: "user",
};

const successType = {
  "en": "Success",
  "tr": "Başarılı",
  "ja": "成功",
}

const LivenessCheck = observer(() => {
  const PAGE_PATH_NAME = "/lc"
  const SPOOF_ERROR = "Liveness check error. Please try again."
  const SCHEMA_ERROR = "An error occurred. Please try again."

  const navigate = useNavigate();
  const { t, i18n: { language } } = useTranslation();


  const [cameraPermission, setCameraPermission] = useState("prompt");
  const [showMessage, setShowMessage] = useState(false);
  const [positionMessage, setPositionMessage] = useState(null);
  const [isPositionReady, setIsPositionReady] = useState(false);
  const [orientation, setOrientation] = useState(getOrientationType());
  const [videoConstraints, setVideoConstraints] = useState(
    getOrientationType() === 'landscape' ? landscapeConstraints : portraitConstraints
  );
  const [showPreloader, setShowPreloader] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLastRequestCompleted, setIsLastRequestCompleted] = useState(false);
  const [wasmDependencyLoading, setWasmDependencyLoading] = useState(true);

  const webcamRef = useRef(null);
  const cameraRef = useRef(null);
  const isProcessingExpressionRef = useRef(false);
  const runningMode = "VIDEO";

  const handleOrientationChange = () => {
    const newOrientation = getOrientationType();
    setOrientation(newOrientation);
    setVideoConstraints(newOrientation === 'landscape' ? landscapeConstraints : portraitConstraints);
  };

  const waitAndNext = () => {
    store.setLoading(true);
    setTimeout(() => {
      store.setLoading(false);
    }, 1000);
  };

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    store.setImg(imageSrc);
    return imageSrc
  }, [webcamRef]);

  const handleFacialExpression = useCallback((response, expression) => {
    if (response[expression] !== true) return;

    const instructionKeys = Object.keys(store.instructionList).filter(key => key.startsWith('prop_') && key !== 'prop_active');
    const totalInstructions = instructionKeys.length;

    for (let i = 0; i < totalInstructions; i++) {
      if (
        store.instructionList.prop_active === i &&
        store.instructionList[`prop_${i}`] === expression
      ) {
        store.setInstructionList({
          ...store.instructionList,
          prop_active: i + 1,
        });

        const action = faceActionMeta.find(
          (x) => x.name === store.instructionList[`prop_${i + 1}`]
        );
        store.setDescription(t(action?.description));

        waitAndNext();

        if (i === totalInstructions - 1) {
          store.setInstructionList({
            ...store.instructionList,
            prop_active: 0,
            is_completed: true,
          });
          const completedAction = faceActionMeta.find(
            (x) => x.name === "completed"
          );
          store.setDescription(t(completedAction?.description));
          store.setInstructionList({
            ...store.instructionList,
            is_completed: false,
          });
        }
        break;
      }
    }
  }, [t]);

  const processExpression = async (response, expression) => {
    const activeStep = store.instructionList.prop_active;
    const expectedExpression = store.instructionList[`prop_${activeStep}`];

    if (!response[expression] || expectedExpression !== expression) {
      return true;
    }

    const expectedImageCount = 1 + activeStep;
    if (applicationStore.livenessImages.length !== expectedImageCount) {
      logger.log("[liveness] duplicate capture prevented", {
        expression,
        activeStep,
        imageCount: applicationStore.livenessImages.length,
        expectedImageCount,
      });
      return true;
    }

    const imageBase64 = response.capturedImage || capture();

    // Re-run the model on the static image before sending the selected frame
    // to the backend to confirm the action was actually detected.
    // If verification fails, reject the frame; reset the window and keep trying
    // until a suitable frame is captured for the same instruction.
    const isVerified = await verifyActionOnImage(imageBase64, expression);
    if (!isVerified) {
      logger.log("frame verification failed, retrying expression:", expression);
      resetStabilization();
      return true;
    }

    if (store.instructionList.prop_active !== activeStep) {
      logger.log("[liveness] step changed during verification, skipping capture", {
        expression,
        activeStep,
        currentStep: store.instructionList.prop_active,
      });
      return true;
    }

    resetStabilization();
    logger.log("image : ", imageBase64 !== null);
    logger.log("using captured frame: ", response.capturedImage !== null);

    handleFacialExpression(response, expression);

    setShowPreloader(true);
    setIsLastRequestCompleted(false);

    applicationStore.setImg(imageBase64)
    applicationStore.setLivenessImages({
      "movement_payload": expression,
      "image": imageBase64,
    })
    setShowPreloader(false);

    if (store?.description !== successType[language]) {
      return true;
    }
    try {
      const shouldIncludeNodeId = String(applicationStore.workflowNextServiceResponse.node_id) === String(applicationStore.lastContextOfStep.node_id);
      const contextOfStep = {
        step: NodeTypes.LIVENESS,
        "retry": applicationStore.workflowNextServiceResponse.retry,
        ...(shouldIncludeNodeId && { node_id: applicationStore.lastContextOfStep.node_id })
      };

      const result = await workflowStepIterator({
        access_token: store.access_token,
        node_id: store.workflowNextServiceResponse.node_id,
        body: {
          "data": [...applicationStore.livenessImages]
        },
        action_index: 1,
        constextOfStep: contextOfStep,
      });

      if (result.data?.service_results[0].status === 200) {
        setShowPreloader(false);
        applicationStore.setWorkflowNextServiceResponse(result.data);
        applicationStore.setLastContextOfStep(contextOfStep);
        if (result.data?.service_results[0]?.data?.spoof === "FAKE") {
          throw new Error(SPOOF_ERROR)
        }

        if (store?.description === successType[language]) {
          setIsLastRequestCompleted(true);
        }
        return true;
      } else if (result.data?.service_results[0].status === 422) {
        throw new Error(SCHEMA_ERROR)
      } else {
        throw new Error(SPOOF_ERROR)
      }
    } catch (error) {
      if (error.message === SCHEMA_ERROR) {
        setErrorMessage("");
        setShowPreloader(true);
        toast.error(SCHEMA_ERROR);
        return false;
      }
      if (error.message === SPOOF_ERROR) {
        setErrorMessage(SPOOF_ERROR);
        setShowPreloader(true);
        toast.error(SPOOF_ERROR);
        return false;
      }
      setShowPreloader(false);
      toast.error(`${expression} image upload failed.`);
      return false;
    }
  };

  const checkFacePosition = useCallback((results) => {
    if (!results.faceLandmarks || results.faceLandmarks.length === 0) {
      return { ready: false, message: t('lc.info.noFaceDetected') };
    }

    const landmarks = results.faceLandmarks[0];

    const leftCheek = landmarks[234];
    const rightCheek = landmarks[454];
    const chin = landmarks[152];
    const forehead = landmarks[10];
    const noseTip = landmarks[1];

    const faceWidth = Math.abs(rightCheek.x - leftCheek.x);
    const faceHeight = Math.abs(chin.y - forehead.y);

    const minWidthRatio = 0.18;
    const minHeightRatio = 0.22;
    const maxWidthRatio = 0.55;
    const maxHeightRatio = 0.75;

    if (faceWidth < minWidthRatio || faceHeight < minHeightRatio) {
      return { ready: false, message: t('lc.info.getCloser') };
    }

    if (faceWidth > maxWidthRatio || faceHeight > maxHeightRatio) {
      return { ready: false, message: t('lc.info.getAway') };
    }

    const noseY = noseTip.y;

    if (noseY < 0.25) {
      return { ready: false, message: t('lc.info.moveDown') };
    }

    if (noseY > 0.75) {
      return { ready: false, message: t('lc.info.moveUp') };
    }

    return { ready: true, message: null };
  }, [t]);

  const onResults = useCallback(
    async (results) => {
      if (results.faceLandmarks && results.faceLandmarks.length > 0) {
        const positionCheck = checkFacePosition(results);

        if (!positionCheck.ready) {
          setIsPositionReady(false);
          setPositionMessage(positionCheck.message);
          setShowMessage(true);
          return;
        }

        setPositionMessage(null);
        setIsPositionReady(true);

        const expectedAction = store.instructionList[`prop_${store.instructionList.prop_active}`];
        const response = await getAction(results, webcamRef.current?.video, expectedAction);

        if (response?.faceQuality && !response.faceQuality.valid) {
          setShowMessage(true);
          setIsPositionReady(false);
          return;
        }

        setShowMessage(false);

        if (!response?.stabilization?.isStabilized) {
          return;
        }

        if (
          response &&
          !store.instructionList.is_completed &&
          !isProcessingExpressionRef.current &&
          controlField(response, store.instructionList[`prop_${store.instructionList.prop_active}`])
        ) {
          isProcessingExpressionRef.current = true;
          resetStabilization();

          const expression = store.instructionList[`prop_${store.instructionList.prop_active}`];

          try {
            await processExpression(response, expression);
          } finally {
            isProcessingExpressionRef.current = false;
          }
        }
      } else {
        setShowMessage(true);
        setIsPositionReady(false);
        setPositionMessage(t('lc.info.noFaceDetected'));
      }
    },
    [handleFacialExpression, checkFacePosition, t]
  );

  const handleCameraAccessDenied = useCallback(() => {
    if (webcamRef.current?.video?.srcObject) {
      const stream = webcamRef.current.video.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
    }

    applicationStore.setCameraError(PAGE_PATH_NAME);
    navigate("/error-access-camera", {
      replace: true,
      state: {
        previousPage: PAGE_PATH_NAME,
      },
    });
  }, [navigate])

  useEffect(() => {

    let intervalId;
    async function checkCameraPermission() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');

        if (videoDevices.length === 0) {
          setCameraPermission("no_camera")
          applicationStore.setCameraError(PAGE_PATH_NAME)
          navigate("/error-no-camera", {
            replace: true,
            state: {
              previousPage: PAGE_PATH_NAME,
            },
          });
          return
        }

        if (navigator.permissions && navigator.permissions.query) {
          const permissionStatus = await navigator.permissions.query({ name: "camera" });
          setCameraPermission(permissionStatus.state);

          if (permissionStatus.state === CameraPermissionStatuses.ACCESS_DENIED) {
            handleCameraAccessDenied();
          }
          //not working on safari
          // permissionStatus.onchange = () => {
          //   setCameraPermission(permissionStatus.state);

          //   if (permissionStatus.state === CameraPermissionStatuses.ACCESS_DENIED) {
          //     handleCameraAccessDenied();
          //   }
          // };
          if (!intervalId) {
            intervalId = setInterval(async () => {
              const updatedStatus = await navigator.permissions.query({ name: "camera" });
              if (cameraPermission !== updatedStatus.state) {
                setCameraPermission(updatedStatus.state);
              }

              if (updatedStatus.state === CameraPermissionStatuses.ACCESS_DENIED) {
                handleCameraAccessDenied();
              }
            }, 2000);
          }
        } else {
          logger.warn("Permissions API not supported on browser.");
        }
      } catch (err) {
        logger.error("Permission query error:", err);
      }
    }
    checkCameraPermission();
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [navigate, handleCameraAccessDenied]);

  useEffect(() => {
    const initializeFaceLandmarker = async () => {
      try {
        const resolver = await FilesetResolver.forVisionTasks(
          "/mediapipe/tasks-vision/wasm" //"https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.3/wasm"
        );

        logger.log(resolver);

        const faceLandmarkerOptions = {
          baseOptions: {
            modelAssetPath: "/model/face_landmarker.task",
            delegate: "CPU",
          },
          outputFaceBlendshapes: true,
          runningMode,
          numFaces: 1,
        };

        const faceMarker = await FaceLandmarker.createFromOptions(
          resolver,
          faceLandmarkerOptions
        );
        window.faceMarker = faceMarker;

        if (
          typeof webcamRef.current !== "undefined" &&
          webcamRef.current !== null
        ) {
          cameraRef.current = new cam.Camera(webcamRef?.current?.video, {
            onFrame: async () => {
              if (window.faceMarker) {
                let startTimeMs = performance.now();
                const result = await window.faceMarker.detectForVideo(
                  webcamRef?.current?.video,
                  startTimeMs
                );
                setWasmDependencyLoading(false);
                onResults(result);
              }
            }
          });
          if (cameraPermission === CameraPermissionStatuses.ACCESS_GRANTED) {
            cameraRef.current.start();
          }
        }
      } catch (error) {
        logger.error("Error initializing face landmarker:", error);
      }
    };

    initializeFaceLandmarker();

    return () => {
      if (cameraRef.current) {
        cameraRef.current.stop();
      }
      disposeFaceMarker();
    };
  }, [onResults, cameraPermission]);

  useEffect(() => {
    // Preload IMAGE landmarker to avoid delay on first verification.
    warmUpImageLandmarker();
  }, []);

  useEffect(() => {
    const webcam = webcamRef.current;
    return () => {
      if (webcam?.video?.srcObject) {
        const stream = webcam.video.srcObject;
        const tracks = stream.getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    resetStabilization();

    const getInstructions = async () => {
      store.setInstructionList({
        ...store.instructionList,
        prop_active: 0,
      });

      let contextOfStep;
      try {
        if (applicationStore.workflowNextServiceResponse?.node_id && applicationStore.lastContextOfStep?.node_id) {
          const shouldIncludeNodeId = String(applicationStore.workflowNextServiceResponse.node_id) === String(applicationStore.lastContextOfStep.node_id);
          logger.log("shouldIncludeNodeId", shouldIncludeNodeId);

          contextOfStep = {
            step: NodeTypes.LIVENESS,
            "retry": applicationStore.workflowNextServiceResponse.retry,
            ...(shouldIncludeNodeId && { node_id: applicationStore.lastContextOfStep.node_id })
          };
        } else {
          contextOfStep = {
            step: NodeTypes.LIVENESS,
            "retry": applicationStore.workflowNextServiceResponse.retry,
          };
        }

        const response = await workflowStepIterator({
          access_token: applicationStore.access_token,
          node_id: applicationStore.workflowNextServiceResponse?.node_id,
          body: {
            "query": {
              client_id: applicationStore.client_id,
              types: "face_action",
            }
          },
          action_index: 0,
          constextOfStep: contextOfStep
        })
        if (response.data?.service_results[0].status !== 200) {
          const rawMessage = response.data?.service_results[0]?.message;
          const parsedMessage = parseServiceErrorMessage(rawMessage);
          throw new Error(parsedMessage);
        } else {
          applicationStore.setWorkflowNextServiceResponse(response.data);
          applicationStore.setLastContextOfStep(contextOfStep);
          store.setInstructionList({
            ...store.instructionList,
            ...response.data.service_results[0].data.instructions,
          });

          logger.log("gelen list", toJS(store.instructionList));
          if (store?.description || store?.description === "") {
            const firstDescription = faceActionMeta.find(
              (x) => x.name === store.instructionList.prop_0
            );
            logger.log("first description", firstDescription);
            store.setDescription(t(firstDescription?.description));
          }
          applicationStore.resetLivenessImages();
          applicationStore.setLivenessImages({
            image: applicationStore.selfie,
            movement_payload: LivenessImageMovementPayloadValues.MATCH
          });
          store.setLoading(false);
        }
      } catch (error) {
        logger.log("error", error);
        toast.error(error?.message || "Something went wrong. Please try again later.")
      }
    };
    getInstructions();
  }, [t]);

  useEffect(() => {
    const handleAsyncEffect = async () => {
      if (store?.description === successType[language] && isLastRequestCompleted) {
        disposeFaceMarker();
        navigate("/next", { replace: true });
      }
    };

    handleAsyncEffect();
  }, [store?.description, isLastRequestCompleted, language]);

  useEffect(() => {
    window.addEventListener('orientationchange', handleOrientationChange);
    return () => window.removeEventListener('orientationchange', handleOrientationChange);
  }, []);

  useEffect(() => {
    if (webcamRef.current) {
      webcamRef.current.video.style.width = '100%';
      webcamRef.current.video.style.height = '100%';
      webcamRef.current.video.style.objectFit = 'cover';
    }
  }, [orientation]);

  const controlField = (obj, field) => {
    if (obj?.hasOwnProperty(field)) {
      if (Object.values(obj).filter(value => value === true).length > 1) {
        return false;
      }
      return obj[field];
    } else {
      return false;
    }
  };

  const getProgressStrokeOffset = () => {
    const instructionKeys = Object.keys(store.instructionList).filter(key => key.startsWith('prop_') && key !== 'prop_active');
    const totalInstructions = instructionKeys.length;

    if (totalInstructions === 0) return 753.9822368615503;

    const circumference = 753.9822368615503;
    const segmentLength = circumference / totalInstructions;

    return circumference - (segmentLength * store.instructionList.prop_active);
  };

  const disposeFaceMarker = () => {
    resetStabilization();

    if (window.faceMarker) {
      try {
        if (typeof window.faceMarker.dispose === 'function') {
          window.faceMarker.dispose();
        }
      } catch (error) {
        logger.error("Error disposing face marker:", error);
      }
      window.faceMarker = null;
    }
    disposeImageLandmarker();
    if (cameraRef.current) {
      try {
        cameraRef.current.stop();
      } catch (error) {
        logger.error("Error stopping camera:", error);
      }
    }
    if (webcamRef.current?.video?.srcObject) {
      const stream = webcamRef.current.video.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
    }
  }

  return (
    <>
      {errorMessage ? (
        <InconsistencyError />
      ) : (
        <div className="absolute inset-0 grid grid-rows-12 h-full text-white">
          <div className="row-span-3 pt-[47px] bg-Gray950 z-50">
            <div className="grid grid-cols-3 px-[46px]">
              <Button
                className=""
                variant="primaryBack"
                onClick={() => {
                  if (webcamRef.current?.video?.srcObject) {
                    const stream = webcamRef.current.video.srcObject;
                    const tracks = stream.getTracks();
                    tracks.forEach(track => track.stop());
                  }
                  disposeFaceMarker();
                  return navigate("/lc-start", { replace: true });
                }}
              >
                <LeftArrowWhite />
              </Button>
              <div className="flex justify-center items-center">
                <div className="w-[48px] h-[48px] mx-auto">
                  <figure>
                    <img
                      alt="LcStartImg"
                      className=""
                      src={LcStartImg}
                    />
                  </figure>
                </div>
              </div>
            </div>
            <div className="text-h5 font-bold text-center mt-6 text-Gray50">{t('lc.info.staySteady')}</div>
          </div>
          <div className="row-span-9 grid grid-rows-24 bg-Gray950">
            <div className="relative row-span-12">
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                className="absolute rounded-t-[16px] h-full w-full scale-x-[-1]"
                forceScreenshotSourceSize
              />
              {
                showPreloader && <img
                  src={applicationStore?.img}
                  alt="Captured"
                  className={`absolute rounded-t-[16px] h-full w-full scale-x-[-1] blur-md -z-10}`}
                />
              }
            </div>
            <div className="absolute top-0 left-0 w-full h-full z-10">
              <div className="relative h-full w-full overflow-hidden">
                <div className="absolute left-1/2 bg-transparent rounded-full h-60 w-60 opacity-80" style={{
                  boxShadow: FLOW_SHADOW.vignettePhoneWide,
                  transform: 'translate(-50%, -50%)',
                  top: '42%',
                }}>
                  {
                    showPreloader && <div className="absolute z-20 w-full h-full flex items-center justify-center">
                      <div className={`inline-block h-16 w-16 border-4 border-t-2 border-t-transparent border-Gray50 rounded-full animate-spin`}></div>
                    </div>
                  }
                </div>
                <div className={`absolute left-1/2 top-42% transition-opacity duration-300 ease-in-out transform -translate-x-1/2 -translate-y-1/2 ${isPositionReady ? 'opacity-100' : 'opacity-30'}`}>
                  <div
                    role="img"
                    aria-label="loading circle"
                    className="rotate-270"
                  >
                    <svg width="280" height="280">
                      <circle
                        cx="140"
                        cy="140"
                        r="120"
                        fill="none"
                        stroke={isPositionReady ? FLOW_VAR.success : FLOW_HEX.ringNeutral}
                        strokeWidth="10px"
                        strokeDasharray="753.9822368615503 753.9822368615503"
                        strokeDashoffset={`-${getProgressStrokeOffset()}`}
                      ></circle>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full row-span-12 z-20">
              <div className="flex h-full justify-center flex-col items-center">
                <div className="text-h5 font-bold text-center text-Gray50 pb-16">
                  <div className="flex items-center px-4">
                    {store.loading || wasmDependencyLoading ? (
                      <>
                        <span>{t('lc.loading')}</span>
                        <div className="ml-2 flex items-center">
                          <div className="inline-block h-4 w-4 border-2 border-t-2 border-t-transparent border-white rounded-full animate-spin"></div>
                        </div>
                      </>
                    ) : (
                      <>
                        {positionMessage ? (
                          <span>{positionMessage}</span>
                        ) : showMessage ? (
                          <span>{t('lc.info.staySteady')}</span>
                        ) : store?.description && (
                          <>
                            {!showPreloader ? (<span>{store?.description}</span>) : (
                              <div className="ml-2 flex items-center">
                                <div className="inline-block h-4 w-4 border-2 border-t-2 border-t-transparent border-white rounded-full animate-spin"></div>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default LivenessCheck;